import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { IcoUser16 } from '@onefootprint/icons';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import type { DeviceVisitEvent } from '@/components/entities/components/details/hooks/use-entity-device-insights';
import { displayForUserAgent, icoForUserAgent } from '@/utils/user-agent';
import useScrollIntoView from '../../hooks/use-scroll-into-view';
import getRegion from '../../utils/get-region';
import DetailsLine from '../details-line';
const ROW_CLASS_NAME = 'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-0.5 rounded-default px-3 py-2 text-left';
type VisitRowProps = {
  id: string;
  visit: DeviceVisitEvent;
  isSelected: boolean;
  /** Changes with each pick of the row's pin, which scrolls the list to it. */
  mapPick?: number;
  /** Has a pin on the map; a row without one has nothing to select. */
  isLocatable: boolean;
  onSelect: (id: string) => void;
};
const VisitRow = t0 => {
  const $0 = _c(90);
  const {
    id,
    visit,
    isSelected,
    mapPick,
    isLocatable,
    onSelect
  } = t0;
  let t1;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deviceInsights"
    };
    $0[0] = t1;
  } else {
    t1 = $0[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const ref = useScrollIntoView(mapPick);
  const {
    path,
    timestamp,
    userAgent,
    ipAddress,
    ipCity,
    ipRegion,
    ipCountry,
    deviceCountry,
    deviceTimeZone,
    signals
  } = visit.event.data;
  let t2;
  if ($0[1] !== deviceCountry || $0[2] !== deviceTimeZone || $0[3] !== formatDateWithTime || $0[4] !== id || $0[5] !== ipAddress || $0[6] !== ipCity || $0[7] !== ipCountry || $0[8] !== ipRegion || $0[9] !== isLocatable || $0[10] !== isSelected || $0[11] !== onSelect || $0[12] !== path || $0[13] !== ref || $0[14] !== signals || $0[15] !== t || $0[16] !== timestamp || $0[17] !== userAgent) {
    const flags = signals.filter(_temp);
    let t3;
    if ($0[19] !== ipCity || $0[20] !== ipRegion) {
      t3 = getRegion(ipCity, ipRegion);
      $0[19] = ipCity;
      $0[20] = ipRegion;
      $0[21] = t3;
    } else {
      t3 = $0[21];
    }
    let t4;
    if ($0[22] !== ipCountry || $0[23] !== t3) {
      t4 = [t3, ipCountry].filter(Boolean);
      $0[22] = ipCountry;
      $0[23] = t3;
      $0[24] = t4;
    } else {
      t4 = $0[24];
    }
    const place = t4.join(", ");
    let t5;
    if ($0[25] !== path) {
      t5 = $ => $.visitPath[path];
      $0[25] = path;
      $0[26] = t5;
    } else {
      t5 = $0[26];
    }
    let t6;
    if ($0[27] !== t || $0[28] !== t5) {
      t6 = t(t5);
      $0[27] = t;
      $0[28] = t5;
      $0[29] = t6;
    } else {
      t6 = $0[29];
    }
    const lead = t6;
    let t7;
    if ($0[30] !== ipAddress || $0[31] !== lead || $0[32] !== place) {
      t7 = [lead, ipAddress, place].filter(Boolean);
      $0[30] = ipAddress;
      $0[31] = lead;
      $0[32] = place;
      $0[33] = t7;
    } else {
      t7 = $0[33];
    }
    const details = t7.join(" \xB7 ");
    let t8;
    if ($0[34] !== t || $0[35] !== userAgent) {
      t8 = userAgent ? displayForUserAgent(userAgent) : t(_temp2);
      $0[34] = t;
      $0[35] = userAgent;
      $0[36] = t8;
    } else {
      t8 = $0[36];
    }
    const name = t8;
    let t9;
    if ($0[37] !== formatDateWithTime || $0[38] !== timestamp) {
      t9 = formatDateWithTime(new Date(timestamp), {
        month: "short",
        year: "numeric"
      });
      $0[37] = formatDateWithTime;
      $0[38] = timestamp;
      $0[39] = t9;
    } else {
      t9 = $0[39];
    }
    const time = t9;
    let t10;
    if ($0[40] !== t) {
      t10 = t(_temp3);
      $0[40] = t;
      $0[41] = t10;
    } else {
      t10 = $0[41];
    }
    let t11;
    if ($0[42] !== deviceCountry || $0[43] !== t10) {
      t11 = {
        label: t10,
        value: deviceCountry
      };
      $0[42] = deviceCountry;
      $0[43] = t10;
      $0[44] = t11;
    } else {
      t11 = $0[44];
    }
    let t12;
    if ($0[45] !== t) {
      t12 = t(_temp4);
      $0[45] = t;
      $0[46] = t12;
    } else {
      t12 = $0[46];
    }
    let t13;
    if ($0[47] !== deviceTimeZone || $0[48] !== t12) {
      t13 = {
        label: t12,
        value: deviceTimeZone
      };
      $0[47] = deviceTimeZone;
      $0[48] = t12;
      $0[49] = t13;
    } else {
      t13 = $0[49];
    }
    let t14;
    if ($0[50] !== t11 || $0[51] !== t13) {
      t14 = [t11, t13].filter(_temp5);
      $0[50] = t11;
      $0[51] = t13;
      $0[52] = t14;
    } else {
      t14 = $0[52];
    }
    const deviceFacts = t14;
    const secondaryText = isSelected ? "text-secondary" : "text-tertiary";
    let t15;
    if ($0[53] !== userAgent) {
      t15 = userAgent ? icoForUserAgent(userAgent, "tertiary", "small") : <IcoUser16 color="tertiary" />;
      $0[53] = userAgent;
      $0[54] = t15;
    } else {
      t15 = $0[54];
    }
    let t16;
    if ($0[55] !== t15) {
      t16 = <span aria-hidden={true} className="row-span-2 mt-0.5 flex">{t15}</span>;
      $0[55] = t15;
      $0[56] = t16;
    } else {
      t16 = $0[56];
    }
    let t17;
    if ($0[57] !== name) {
      t17 = <span className="truncate text-label-2 text-primary @max-md:col-span-2 @max-md:wrap-anywhere @max-md:whitespace-normal" data-dd-privacy="mask">{name}</span>;
      $0[57] = name;
      $0[58] = t17;
    } else {
      t17 = $0[58];
    }
    let t18;
    if ($0[59] !== secondaryText) {
      t18 = cx("whitespace-nowrap text-body-3 @max-md:col-span-2 @max-md:col-start-2 @max-md:row-start-3", secondaryText);
      $0[59] = secondaryText;
      $0[60] = t18;
    } else {
      t18 = $0[60];
    }
    let t19;
    if ($0[61] !== t18 || $0[62] !== time) {
      t19 = <span className={t18}>{time}</span>;
      $0[61] = t18;
      $0[62] = time;
      $0[63] = t19;
    } else {
      t19 = $0[63];
    }
    let t20;
    if ($0[64] !== secondaryText) {
      t20 = cx("col-span-2 truncate text-body-3 @max-md:wrap-anywhere @max-md:whitespace-normal", secondaryText);
      $0[64] = secondaryText;
      $0[65] = t20;
    } else {
      t20 = $0[65];
    }
    let t21;
    if ($0[66] !== ipAddress || $0[67] !== lead || $0[68] !== place) {
      t21 = <DetailsLine lead={lead} ipAddress={ipAddress} place={place} />;
      $0[66] = ipAddress;
      $0[67] = lead;
      $0[68] = place;
      $0[69] = t21;
    } else {
      t21 = $0[69];
    }
    let t22;
    if ($0[70] !== t20 || $0[71] !== t21) {
      t22 = <span className={t20} data-dd-privacy="mask">{t21}</span>;
      $0[70] = t20;
      $0[71] = t21;
      $0[72] = t22;
    } else {
      t22 = $0[72];
    }
    let t23;
    if ($0[73] !== t16 || $0[74] !== t17 || $0[75] !== t19 || $0[76] !== t22) {
      t23 = <>{t16}{t17}{t19}{t22}</>;
      $0[73] = t16;
      $0[74] = t17;
      $0[75] = t19;
      $0[76] = t22;
      $0[77] = t23;
    } else {
      t23 = $0[77];
    }
    const content = t23;
    const t24 = isLocatable && (isSelected ? "bg-hover-control" : "hover:bg-hover");
    let t25;
    if ($0[78] !== t24) {
      t25 = cx("relative scroll-my-6 rounded-default transition-colors duration-100", t24);
      $0[78] = t24;
      $0[79] = t25;
    } else {
      t25 = $0[79];
    }
    let t26;
    if ($0[80] !== details || $0[81] !== id || $0[82] !== isLocatable || $0[83] !== isSelected || $0[84] !== name || $0[85] !== onSelect || $0[86] !== time) {
      t26 = isLocatable && <button type="button" aria-current={isSelected || undefined} aria-label={[name, time, details].join(" ")} onClick={() => onSelect(id)} className="absolute inset-0 cursor-pointer rounded-default" />;
      $0[80] = details;
      $0[81] = id;
      $0[82] = isLocatable;
      $0[83] = isSelected;
      $0[84] = name;
      $0[85] = onSelect;
      $0[86] = time;
      $0[87] = t26;
    } else {
      t26 = $0[87];
    }
    let t27;
    if ($0[88] !== content) {
      t27 = <div className={ROW_CLASS_NAME}>{content}</div>;
      $0[88] = content;
      $0[89] = t27;
    } else {
      t27 = $0[89];
    }
    t2 = <li ref={ref} className={t25}>{t26}{t27}{(deviceFacts.length > 0 || flags.length > 0) && <div className="-mt-1.5 flex flex-col gap-1.5 pr-3 pb-2 pl-10 text-body-3">{deviceFacts.length > 0 && <dl className="flex flex-wrap gap-x-3 gap-y-0.5" data-dd-privacy="mask">{deviceFacts.map(t28 => {
            const {
              label,
              value: value_0
            } = t28;
            return <div key={label} className="flex min-w-0 flex-wrap gap-x-1"><dt className={cx("whitespace-nowrap", secondaryText)}>{label}</dt><dd className="min-w-0 text-secondary wrap-anywhere">{value_0}</dd></div>;
          })}</dl>}{flags.length > 0 && <dl className="flex min-w-0 flex-wrap gap-x-1"><dt className={cx("whitespace-nowrap", secondaryText)}>{t(_temp6)}</dt><dd className="min-w-0 text-secondary">{flags.map(flag => t($_4 => $_4.flag[flag])).join(", ")}</dd></dl>}</div>}</li>;
    $0[1] = deviceCountry;
    $0[2] = deviceTimeZone;
    $0[3] = formatDateWithTime;
    $0[4] = id;
    $0[5] = ipAddress;
    $0[6] = ipCity;
    $0[7] = ipCountry;
    $0[8] = ipRegion;
    $0[9] = isLocatable;
    $0[10] = isSelected;
    $0[11] = onSelect;
    $0[12] = path;
    $0[13] = ref;
    $0[14] = signals;
    $0[15] = t;
    $0[16] = timestamp;
    $0[17] = userAgent;
    $0[18] = t2;
  } else {
    t2 = $0[18];
  }
  return t2;
};
export default VisitRow;
function _temp(signal) {
  return typeof signal === "string";
}
function _temp2($_0) {
  return $_0.noDeviceData;
}
function _temp3($_1) {
  return $_1.deviceCountry;
}
function _temp4($_2) {
  return $_2.deviceTimeZone;
}
function _temp5(t0) {
  const {
    value
  } = t0;
  return value;
}
function _temp6($_3) {
  return $_3.flags;
}